"use client"

import Link from "next/link"
import VideoCard from "@/components/video-card"
import GifCard from "@/components/gif-card"
import MemeTemplateCard from "@/components/meme-template-card"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Trash2, Loader2, Music, Wand2 } from "lucide-react"
import type { Video, VideoDetail } from "@/lib/api"
import { getMediaUrl } from "@/lib/api"
import {
  generateSoundEffectUrl,
  generateTitleSlug,
  cn,
} from "@/lib/utils"

const GRID =
  "grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-3 gap-y-4 sm:gap-x-4 sm:gap-y-5"

export type ProfileUploadContentType =
  | "meme-videos"
  | "meme-templates"
  | "gifs"
  | "sound-effects"
  | "generated-memes"

function normalizeVideoForCard(item: any, profileUser: { id: number; username: string; profilePicture?: string | null; verified?: boolean }) {
  return {
    ...item,
    user:
      item.user ||
      ({
        id: profileUser.id,
        username: profileUser.username,
        profilePicture: profileUser.profilePicture ?? undefined,
        verified: profileUser.verified,
      } as any),
  }
}

function moderationCornerBadge(item: any) {
  if (!item?.status || item.status === "pending") return null
  if (item.status !== "rejected" && item.status !== "approved") return null
  return (
    <Badge
      variant="secondary"
      className={cn(
        "pointer-events-none absolute left-2 top-2 z-[6] text-[10px] font-bold uppercase",
        item.status === "rejected" ? "bg-red-950/90 text-red-300 border-red-800" : "bg-emerald-950/90 text-emerald-300 border-emerald-800"
      )}
    >
      {item.status}
    </Badge>
  )
}

interface UserProfileUploadGridProps {
  contentType: ProfileUploadContentType
  items: any[]
  isOwnProfile: boolean
  profileUser: { id: number; username: string; profilePicture?: string | null; verified?: boolean }
  deletingId: number | null
  onVideoClick: (video: Video | VideoDetail) => void
  onOpenMemeEditor: (item: any) => void
  onDelete: (item: { id: number; title?: string | null }) => void
}

export function UserProfileUploadGrid({
  contentType,
  items,
  isOwnProfile,
  profileUser,
  deletingId,
  onVideoClick,
  onOpenMemeEditor,
  onDelete,
}: UserProfileUploadGridProps) {
  if (contentType === "meme-videos") {
    return (
      <div className={GRID}>
        {items.map((item) => {
          const v = normalizeVideoForCard(item, profileUser)
          return (
            <div key={`mv-${item.id}`} className="relative min-w-0">
              <VideoCard video={v} onVideoClick={onVideoClick} />
              {moderationCornerBadge(item)}
              {isOwnProfile && (
                <div className="pointer-events-none absolute right-2 top-2 z-[8] flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="pointer-events-auto h-8 w-8 p-0 bg-zinc-900/95 border border-red-900/60 text-red-400 shadow-md hover:bg-red-950/80"
                    onClick={() => onDelete(item)}
                    disabled={deletingId === item.id}
                    title="Delete"
                  >
                    {deletingId === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  if (contentType === "meme-templates") {
    return (
      <div className={GRID}>
        {items.map((item) => (
          <div key={`mt-${item.id}`} className="relative min-w-0 h-full">
            <MemeTemplateCard template={item} viewMode="grid" />
            {moderationCornerBadge(item)}
            {isOwnProfile && (
              <div className="pointer-events-none absolute right-2 top-2 z-[8] flex gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="pointer-events-auto h-8 w-8 p-0 bg-zinc-900/95 border border-red-900/60 text-red-400 shadow-md"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onDelete(item)
                  }}
                  disabled={deletingId === item.id}
                  title="Delete"
                >
                  {deletingId === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  if (contentType === "gifs") {
    return (
      <div className={GRID}>
        {items.map((item) => (
          <div key={`gif-${item.id}`} className="relative min-w-0 h-full">
            <GifCard gif={item} viewMode="grid" />
            {moderationCornerBadge(item)}
            {isOwnProfile && (
              <div className="pointer-events-none absolute right-2 top-2 z-[8] flex gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="pointer-events-auto h-8 w-8 p-0 bg-zinc-900/95 border border-red-900/60 text-red-400 shadow-md"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onDelete(item)
                  }}
                  disabled={deletingId === item.id}
                  title="Delete"
                >
                  {deletingId === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  if (contentType === "generated-memes") {
    return (
      <div className={GRID}>
        {items.map((item) => {
          const slug =
            (typeof item.slug === "string" && item.slug.trim()) ||
            generateTitleSlug(item.title || "meme") ||
            String(item.id)
          const href = `/meme-generator/${encodeURIComponent(slug)}`
          const src = getMediaUrl(item.image_url || item.image_path || "")
          return (
            <div key={`gm-${item.id}`} className="relative min-w-0">
              <Link href={href} className="group block h-full">
                <Card className="h-full border-zinc-800 bg-zinc-900 transition-colors hover:border-zinc-600">
                  <CardContent className="p-0">
                    <div className="relative aspect-square overflow-hidden rounded-t-xl bg-zinc-950">
                      {src ? (
                        <img
                          src={src}
                          alt={item.title || "Meme"}
                          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-zinc-600">
                          <Wand2 className="h-10 w-10" />
                        </div>
                      )}
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-8">
                        <p className="line-clamp-2 text-xs font-semibold text-white">{item.title || "Untitled"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              {isOwnProfile && (
                <div className="pointer-events-none absolute right-2 top-2 z-[8] flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="pointer-events-auto h-8 w-8 p-0 bg-zinc-900/95 border border-zinc-700 shadow-md"
                    onClick={(e) => {
                      e.preventDefault()
                      onOpenMemeEditor(item)
                    }}
                    title="Open in editor"
                  >
                    <Wand2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="pointer-events-auto h-8 w-8 p-0 bg-zinc-900/95 border border-red-900/60 text-red-400 shadow-md"
                    onClick={(e) => {
                      e.preventDefault()
                      onDelete(item)
                    }}
                    disabled={deletingId === item.id}
                    title="Delete"
                  >
                    {deletingId === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  /* sound-effects — compact tiles (detail uses sound-effects route) */
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {items.map((item) => {
        const title = (item.title || item.name || "Sound").trim()
        const href = generateSoundEffectUrl(item.id, title)
        const thumb =
          item.thumbnail_url ||
          item.thumbnail_path ||
          item.image_url ||
          item.cover_path ||
          null
        return (
          <div key={`sfx-${item.id}`} className="relative">
            <Link
              href={href}
              className="flex flex-col gap-2 rounded-xl border border-zinc-800 bg-zinc-900/80 p-3 transition-colors hover:border-zinc-600 hover:bg-zinc-900"
            >
              <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-zinc-950">
                {thumb ? (
                  <img src={getMediaUrl(thumb)} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <Music className="h-8 w-8 text-zinc-600" />
                )}
              </div>
              <p className="line-clamp-2 text-center text-xs font-medium text-zinc-200">{title}</p>
            </Link>
            {moderationCornerBadge(item)}
            {isOwnProfile && (
              <div className="pointer-events-none absolute right-1 top-1 z-[8] flex gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="pointer-events-auto h-8 w-8 p-0 bg-zinc-900/95 border border-red-900/60 text-red-400 shadow-md"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onDelete(item)
                  }}
                  disabled={deletingId === item.id}
                  title="Delete"
                >
                  {deletingId === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
